import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import './ReviewProposal.css';

const ReviewAssignedProposal = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [comments, setComments] = useState({});
  const [historyByDoc, setHistoryByDoc] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [openDocPath, setOpenDocPath] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const roleId = Number(localStorage.getItem('role_id'));
  const token = localStorage.getItem('token');
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/proposals/details/${proposalId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setProposal(data);
        setHistoryByDoc(data?.reviewHistoryByDoc || {});

        const initial = {};
        (data?.documents || []).forEach((doc) => {
          initial[doc.document_id] = {
            action: doc.reviewer_action || '',
            comment: doc.reviewer_comment || '',
          };
        });
        setComments(initial);
        setIsEditing(false);
      } catch (err) {
        console.error('Error fetching proposal details:', err);
      }
    };
    fetchDetails();
  }, [proposalId, API_BASE_URL, token]);

  const handleCommentChange = (docId, value) => {
    setComments((prev) => ({ ...prev, [docId]: { ...prev[docId], comment: value } }));
  };
  const handleActionChange = (docId, value) => {
    setComments((prev) => ({ ...prev, [docId]: { ...prev[docId], action: value } }));
  };

  if (!proposal) return <div className="empty-state">Loading…</div>;

  const submissionState = proposal.submissionState || {};
  const lockedByStatus =
    proposal.proposal.status === 'Pending Final Approval' ||
    proposal.proposal.status === 'Approved';
  const isReviewer = roleId === 3;
  const reviewerSubmitted = !!submissionState.reviewerSubmitted;
  const canEdit = !!submissionState.canEdit && !lockedByStatus;

  const inputsDisabled = !isReviewer || lockedByStatus || (reviewerSubmitted && !isEditing);

  const submit = async (submit_type) => {
    const document_reviews = Object.entries(comments).map(([document_id, v]) => ({
      document_id,
      comment: v.comment || '',
      action: v.action || 'comment',
    }));

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/reviewer-review/${proposalId}`,
        { proposal_comment: overallComment, document_reviews, submit_type },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(
        data?.message ||
          (submit_type === 'finalize'
            ? 'Your review is finalized.'
            : submit_type === 'request_changes'
            ? 'Sent to Secretary.'
            : 'Feedback saved.')
      );
      navigate('/proposal/view-submissions');
    } catch (err) {
      console.error('Error submitting review:', err);
      alert(err?.response?.data?.error || 'Something went wrong.');
    }
  };

  const withToken = (url) => {
    const token = localStorage.getItem('token');
    if (!token || !url.includes('/api/files/')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}token=${encodeURIComponent(token)}`;
  };

  const openModal = (path) => setOpenDocPath(withToken(`${API_BASE_URL}${path}`));
  const closeModal = () => setOpenDocPath(null);

  const toTitle = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="proposal-details-container">
      {}
      <div className="details-header">
        <h2>Review Proposal: {proposal.proposal?.title}</h2>
        <div className="btn-row">
          {isReviewer && reviewerSubmitted && canEdit && !isEditing && (
<button type="button" className="btn primary sm" onClick={() => setIsEditing(true)}>
  Edit My Feedback
</button>
          )}
          {isReviewer && reviewerSubmitted && isEditing && (
            <button type="button" className="btn sm ghost" onClick={() => setIsEditing(false)}>
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <table className="table-std review-table dense">
          <thead>
            <tr>
              <th>#</th>
              <th>Document Type</th>
              <th>Preview</th>
              <th>Current Status</th>
              <th>Action</th>
              <th>Comment</th>
              <th>History</th>
            </tr>
          </thead>
          <tbody>
            {proposal.documents.map((doc, index) => {
              const hist = historyByDoc[doc.document_id] || [];
              const statusRaw = doc.status || 'Pending';
              const statusKey = String(statusRaw).toLowerCase().replace(/\s+/g, '-');

              return (
                <tr
                  key={doc.document_id}
                  className={doc.fresh_resubmission ? 'row-highlight' : undefined}
                >
                  <td>{index + 1}</td>
                  <td>{doc.document_name}</td>
                  <td>
                <button className="btn primary xs" onClick={() => openModal(doc.path)}>
                  View
                </button>
                  </td>

                  <td>
                    <div className="status-cell">
                      <span className={`document-status ${statusKey}`}>{toTitle(statusRaw)}</span>
                      {doc.fresh_resubmission && (
                        <span className="status-new-tag" title="New resubmission within 72h">
                          NEW
                        </span>
                      )}
                    </div>
                  </td>

                  <td>
                    <select
                      className="select-std"
                      disabled={inputsDisabled}
                      value={comments[doc.document_id]?.action || ''}
                      onChange={(e) => handleActionChange(doc.document_id, e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      <option value="clarification">Request Clarification</option>
                      <option value="revision">Request Revision</option>
                      <option value="resubmit">Request Resubmission</option>
                    </select>
                  </td>

                  <td>
                    <textarea
                      className="textarea-std"
                      rows="2"
                      disabled={inputsDisabled}
                      value={comments[doc.document_id]?.comment || ''}
                      onChange={(e) => handleCommentChange(doc.document_id, e.target.value)}
                      placeholder="Enter comment…"
                    />
                  </td>

                  <td>
                    {hist.length ? (
                      <details>
                        <summary>{hist.length} item(s)</summary>
                        <ul className="history-list">
                          {hist.map((h, i) => (
                            <li key={i}>
                              <div className="comment-meta">
                                {h.reviewed_at ? new Date(h.reviewed_at).toLocaleString() : ''}
                                {' • '}
                                {h.status}
                              </div>
                              <div className="comment-text">{h.comment || '—'}</div>
                            </li>
                          ))}
                        </ul>
                      </details>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <p className="section-title">General Comment (optional)</p>
        <textarea
          className="textarea-std"
          rows="4"
          disabled={inputsDisabled}
          placeholder="Write your overall feedback here…"
          value={overallComment}
          onChange={(e) => setOverallComment(e.target.value)}
        />
      </div>

      <div className="btn-row" style={{ marginTop: 16 }}>
<div className="card">
  <div className="btn-row nowrap center" style={{ gap: 12 }}>
    <button
      className="btn primary"
      disabled={inputsDisabled}
      onClick={() => submit('request_changes')}
      title="Use this if you requested clarification/revision/resubmission on any document"
    >
      Send to Secretary
    </button>

    <button
      className="btn primary"
      disabled={inputsDisabled}
      onClick={() => submit('finalize')}
      title="Finalize your review (no blocking actions)"
    >
      Finalize My Review
    </button>
  </div>
</div>


        <button
          className="btn ghost"
          disabled={inputsDisabled}
          onClick={() => submit('save')}
          title="Save without changing status"
        >
          Save Draft
        </button>
      </div>

      {openDocPath && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <iframe
              className="doc-frame"
              src={openDocPath}
              title="Document Preview"
            />
            <button className="close-modal" onClick={closeModal} aria-label="Close">
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewAssignedProposal;
